import { RequiredDocuments } from '@modules/opportunities/entities/opportunity/api/dto/vault.dto';
import useUploadFileToS3 from '@modules/opportunities/entities/document/mutation/useUploadFileToS3';
import useGetUploadPreSignedUrls from '@modules/opportunities/entities/document/mutation/useGetUploadPreSignedUrls';
import { USER_EVENTS } from '@shared/analytics/events';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import {
  Dispatch, SetStateAction, useContext
} from 'react';
import {
  BulkUpload,
  DocumentOperationsProvider,
} from '@huspy/forge/features';
import { FileToUpload } from '@huspy/forge/types';
import useDownloadDocument from '@modules/opportunities/entities/document/mutation/useDownloadDocument';
import useCategorizeDocuments from '@modules/opportunities/entities/document/mutation/useCategorizeDocuments';
import useDeleteApplicantDocument from '@modules/opportunities/entities/document/mutation/useDeleteApplicantDocument';
import { UnsavedChangesSlido } from '@modules/opportunities/presentation/pages/CaseCreation/features/UnsavedChangesSlido';
import { CaseCreationContext } from '@modules/opportunities/presentation/pages/CaseCreation';

type Props = {
  applicantId: string;
  documents: RequiredDocuments;
  applicantName: string;
  oppId: string;
};

export const BulkUploadWrapper = ({
  applicantId,
  documents,
  oppId,
  applicantName,
}: Props) => {
  const { mutateAsync: getGetUploadPresignedUrls } = useGetUploadPreSignedUrls(
    oppId,
    applicantId
  );
  const { mutateAsync: uploadFileWithPresignedUrl } = useUploadFileToS3(
    oppId,
    applicantId
  );
  const fetchPresignedUrls = async (files: FileToUpload[]) =>
    getGetUploadPresignedUrls({
      opportunityExternalID: oppId,
      opportunityApplicantExternalID: applicantId,
      filesNames: files.map((file) => file.fileName),
    });

  const uploadSingleFile = async (
    file: FileToUpload | undefined,
    presignedUrl: { url: string; fields: any },
    setNumUploadedDoc: Dispatch<SetStateAction<number>>
  ) => {
    if (!file) {
      return;
    }
    await uploadFileWithPresignedUrl({
      url: presignedUrl.url,
      fields: presignedUrl.fields,
      opportunityExternalID: oppId,
      opportunityApplicantExternalID: applicantId,
      body: file.body,
    });
    setNumUploadedDoc((prev) => prev + 1);
  };

  const uploadDocumentToS3 = async (
    files: FileToUpload[],
    setNumUploadedDoc: Dispatch<SetStateAction<number>>,
    reset: () => void
  ) => {
    if (files.length === 0) return;

    try {
      const presignedUrls = await fetchPresignedUrls(files);

      await Promise.all(
        presignedUrls.response.map((res, idx) =>
          uploadSingleFile(files[idx], res, setNumUploadedDoc))
      );

      trackAmplitudeEvent(
        USER_EVENTS.CASE.CASE_BANK_DOCUMENT_UPLOAD_COMPLETED,
        { useContext: true }
      );
    } catch (error) {
      trackAmplitudeEvent(USER_EVENTS.CASE.CASE_BANK_DOCUMENT_UPLOAD_FAILED, {
        useContext: true,
        error_message: error,
      });
      reset();
      throw new Error('Error while uploading the document');
    }
  };

  const { goToNextState } = useContext(CaseCreationContext);

  return (
    <DocumentOperationsProvider
      operations={ {
        useCategorizeDocuments,
        useDownloadDocument,
        useDeleteDocument: useDeleteApplicantDocument,
        goNext: goToNextState,
        ExitConfirmation: UnsavedChangesSlido,
      } }
    >
      <BulkUpload
        key={ applicantId }
        applicantId={ applicantId }
        oppId={ oppId }
        documents={ documents }
        applicantName={ applicantName }
        uploadDocumentToS3={ uploadDocumentToS3 }
      />
    </DocumentOperationsProvider>
  );
};
